/**
 * Der Social-Kalender (ab 0.15.0): alle Beitraege eines Monats — aus dem
 * Monatsplan, aus Aktionen und die eigenen —, und eigene anlegen.
 *
 * Ein eigener Beitrag braucht kein Modell und kein Kontingent und darf an
 * jedem Tag stehen. „Text vorschlagen“ ist die eine Stelle mit Modell: Sie
 * zaehlt gegen das Kontingent des Vertrags und speichert nichts — der Kunde
 * uebernimmt den Vorschlag ins Feld und bearbeitet ihn.
 */

import { useMemo, useState } from "react";

import type { HubMedium, HubSocialPost } from "../core/types.js";
import { useMediathek, useSocialKalender } from "./hooks.js";
import { fehlerText, KANAL, lokal, PostKarte } from "./Planung.js";

export interface SocialKalenderProps {
  basePath?: string;
  userName?: string;
}

const z = (n: number) => String(n).padStart(2, "0");
const iso = (d: Date) => `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;

function monatsGrenzen(versatz: number): { von: string; bis: string; titel: string } {
  const heute = new Date();
  const erster = new Date(heute.getFullYear(), heute.getMonth() + versatz, 1);
  const letzter = new Date(erster.getFullYear(), erster.getMonth() + 1, 0);
  return {
    von: iso(erster),
    bis: iso(letzter),
    titel: erster.toLocaleDateString("de-DE", { month: "long", year: "numeric" }),
  };
}

function tagTitel(isoZeit: string): string {
  return new Date(isoZeit).toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" });
}

export function SocialKalender({ basePath, userName = "" }: SocialKalenderProps) {
  const [versatz, setVersatz] = useState(0);
  const monat = monatsGrenzen(versatz);
  const k = useSocialKalender(monat.von, monat.bis, basePath);
  const [neu, setNeu] = useState(false);

  const tage = useMemo(() => {
    const gruppen: { tag: string; titel: string; posts: HubSocialPost[] }[] = [];
    for (const p of k.data?.posts ?? []) {
      if (!p.scheduled_at) continue;
      const tag = iso(new Date(p.scheduled_at));
      if (tag < monat.von || tag > monat.bis) continue;
      const letzte = gruppen[gruppen.length - 1];
      if (letzte && letzte.tag === tag) letzte.posts.push(p);
      else gruppen.push({ tag, titel: tagTitel(p.scheduled_at), posts: [p] });
    }
    return gruppen;
  }, [k.data, monat.von, monat.bis]);

  return (
    <div className="ch-kalender">
      <div className="ch-kopf">
        <span className="ch-aktionen" style={{ margin: 0 }}>
          <button type="button" className="ch-knopf" onClick={() => setVersatz((v) => v - 1)} aria-label="Vorheriger Monat">‹</button>
          <strong style={{ minWidth: 140, textAlign: "center" }}>{monat.titel}</strong>
          <button type="button" className="ch-knopf" onClick={() => setVersatz((v) => v + 1)} aria-label="Nächster Monat">›</button>
        </span>
        <button type="button" className="ch-knopf ch-knopf-primaer" onClick={() => setNeu((v) => !v)}>
          {neu ? "Schließen" : "+ Beitrag"}
        </button>
      </div>

      {neu && k.data && (
        <NeuerBeitrag kanaele={k.data.kanaele} userName={userName} basePath={basePath}
          vorschlag={k.vorschlag}
          anlegen={async (post) => { await k.anlegen(post); setNeu(false); }} />
      )}

      {k.loading && !k.data ? (
        <p className="ch-muted">Lädt …</p>
      ) : k.error ? (
        <p className="ch-hinweis">Der Kalender konnte nicht geladen werden: {k.error}</p>
      ) : tage.length === 0 ? (
        <p className="ch-muted">In diesem Monat ist nichts geplant.</p>
      ) : (
        tage.map((t) => (
          <div key={t.tag}>
            <p className="ch-woche">{t.titel}</p>
            <ul className="ch-karten">
              {t.posts.map((p) => (
                <PostKarte key={`${p.id}-${p.status}`} post={p} userName={userName}
                  aendern={k.aendern} gepostet={k.gepostet} loeschen={k.loeschen} erneut={k.erneut} />
              ))}
            </ul>
          </div>
        ))
      )}
    </div>
  );
}

interface NeuProps {
  kanaele: string[];
  userName: string;
  basePath?: string;
  vorschlag: ReturnType<typeof useSocialKalender>["vorschlag"];
  anlegen: (post: Parameters<ReturnType<typeof useSocialKalender>["anlegen"]>[0]) => Promise<void>;
}

function morgenAbend(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(18, 30, 0, 0);
  return lokal(d.toISOString());
}

function NeuerBeitrag({ kanaele, userName, basePath, vorschlag, anlegen }: NeuProps) {
  const mediathek = useMediathek(basePath);
  const [kanal, setKanal] = useState(kanaele.includes("instagram") ? "instagram" : kanaele[0] ?? "instagram");
  const [wann, setWann] = useState(morgenAbend());
  const [medien, setMedien] = useState<number[]>([]);
  const [caption, setCaption] = useState("");
  const [tags, setTags] = useState("");
  const [hinweis, setHinweis] = useState("");
  const [freigeben, setFreigeben] = useState(true);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const waehlbar: HubMedium[] = (mediathek.data ?? []).filter((m) => m.status === "bereit" && m.approved_for_use);
  const art = (id: number) => waehlbar.find((m) => m.id === id)?.kind;

  function umschalten(id: number) {
    setMedien((liste) => {
      if (liste.includes(id)) return liste.filter((x) => x !== id);
      // Ein Video steht allein; zu einem Video kommt kein Bild dazu.
      if (art(id) === "video" || liste.some((x) => art(x) === "video")) return [id];
      return [...liste, id].slice(0, 10);
    });
  }

  async function vorschlagHolen() {
    setBusy(true);
    setFehler(null);
    setInfo(null);
    try {
      const v = await vorschlag({ channel: kanal, media_ids: medien, hinweis, bisher: caption });
      setCaption(v.caption);
      setTags(v.hashtags.join(" "));
      if (v.kontingent) setInfo(`Vorschlag übernommen — ${v.verbraucht} von ${v.kontingent} in diesem Monat.`);
    } catch (e) {
      setFehler(fehlerText(e).text);
    } finally {
      setBusy(false);
    }
  }

  async function speichern(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFehler(null);
    try {
      await anlegen({
        channel: kanal, scheduled_at: new Date(wann).toISOString(), caption,
        hashtags: tags.split(/\s+/).filter(Boolean), media_ids: medien, by: userName,
        // TikTok braucht erst die Angaben an der Karte — freigegeben wird danach.
        freigeben: kanal === "tiktok" ? false : freigeben,
      });
    } catch (err) {
      setFehler(fehlerText(err).text);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="ch-karte ch-formular" onSubmit={speichern} style={{ marginBottom: 14 }}>
      <div className="ch-auswahl">
        <label>
          Kanal{" "}
          <select className="ch-eingabe" value={kanal} onChange={(e) => setKanal(e.target.value)}>
            {kanaele.map((c) => <option key={c} value={c}>{KANAL[c] ?? c}</option>)}
          </select>
        </label>
        <label>
          Termin <input className="ch-eingabe" type="datetime-local" value={wann} onChange={(e) => setWann(e.target.value)} required />
        </label>
      </div>

      <p className="ch-klein ch-muted" style={{ margin: "4px 0" }}>
        {kanal === "tiktok"
          ? "TikTok: genau ein Video. Sichtbarkeit und die übrigen Angaben setzen Sie danach an der Karte."
          : "Fotos und Videos — aus der Mediathek, zur Verwendung freigegeben. Das erste ist das Titelbild."}
      </p>
      {mediathek.loading && !mediathek.data ? (
        <p className="ch-muted ch-klein">Lädt …</p>
      ) : waehlbar.length === 0 ? (
        <p className="ch-muted ch-klein">Noch keine freigegebenen Medien — erst in der Mediathek hochladen und freigeben.</p>
      ) : (
        <div className="ch-post-medien ch-medienwahl">
          {waehlbar.map((m) => {
            const nr = medien.indexOf(m.id);
            return (
              <button key={m.id} type="button" onClick={() => umschalten(m.id)} aria-pressed={nr >= 0}
                className={nr >= 0 ? "ch-gewaehlt" : ""} title={m.description || m.filename}>
                {m.preview_url ? <img src={m.preview_url} alt={m.description || m.filename} loading="lazy" />
                  : <span className="ch-klein">{m.kind === "video" ? "Video" : "Bild"}</span>}
                {nr >= 0 && <span className="ch-nummer">{nr + 1}</span>}
              </button>
            );
          })}
        </div>
      )}

      <label className="ch-klein ch-muted" htmlFor="ch-neu-text">Text</label>
      <textarea id="ch-neu-text" className="ch-feld" rows={5} value={caption} onChange={(e) => setCaption(e.target.value)} />
      <label className="ch-klein ch-muted" htmlFor="ch-neu-tags">Hashtags</label>
      <input id="ch-neu-tags" className="ch-eingabe" value={tags} onChange={(e) => setTags(e.target.value)} />

      <div className="ch-aktionen">
        <input className="ch-eingabe" style={{ flex: "1 1 220px", margin: 0 }} value={hinweis}
          onChange={(e) => setHinweis(e.target.value)} placeholder="Worum soll es gehen? (für den Vorschlag)" />
        <button type="button" className="ch-knopf" disabled={busy} onClick={() => void vorschlagHolen()}>
          Text vorschlagen
        </button>
      </div>

      <label className="ch-klein" style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 8 }}>
        <input type="checkbox" checked={freigeben} onChange={(e) => setFreigeben(e.target.checked)} /> Gleich freigeben
      </label>
      <div className="ch-aktionen">
        <button type="submit" className="ch-knopf ch-knopf-primaer" disabled={busy}>Beitrag anlegen</button>
      </div>
      {info && <p className="ch-muted ch-klein">{info}</p>}
      {fehler && <p className="ch-hinweis">{fehler}</p>}
    </form>
  );
}
